"use client";

import { useLayoutEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * These are the public (unauthenticated) route prefixes.
 * Any route NOT starting with one of these is treated as authenticated
 * and will respect the user's dark mode preference.
 * Public pages are always forced to light mode.
 */
const PUBLIC_ROUTE_PREFIXES = [
  "/login",
  "/about",
  "/blog",
  "/events",
  "/faq",
  "/features",
  "/jobfit-report",
  "/my_story",
  "/pricing",
  "/privacy",
  "/resume-check",
  "/revvrally",
  "/support",
  "/terms",
];

function isPublicRoute(pathname: string): boolean {
  if (pathname === "/") return true;
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * RootThemeProvider
 *
 * Placed at the root layout level so it is ALWAYS mounted and never unmounts
 * during client-side navigation. By watching `pathname` with useLayoutEffect,
 * the dark class is applied or removed synchronously after React commits the
 * new route's DOM but BEFORE the browser paints — eliminating the
 * light→dark flash that occurs when the effect runs post-paint.
 *
 * Public pages: always light mode (dark class removed).
 * Authenticated pages: dark class applied from localStorage preference or
 * system preference.
 */
export function RootThemeProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  useLayoutEffect(() => {
    const root = document.documentElement;

    if (isPublicRoute(pathname)) {
      root.classList.remove("dark");
      return;
    }

    // Authenticated route — apply user's stored preference or system default.
    // The app stores dark mode under 'hirecarta_dark_mode' or
    // 'hirecarta_dark_mode_<userId>'. Scan for either so we don't need the
    // userId (which isn't available here before the session loads).
    try {
      let stored: string | null = null;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("hirecarta_dark_mode")) {
          stored = localStorage.getItem(key);
          break;
        }
      }

      if (stored !== null) {
        const isDark = JSON.parse(stored) === true;
        isDark ? root.classList.add("dark") : root.classList.remove("dark");
      } else {
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        prefersDark ? root.classList.add("dark") : root.classList.remove("dark");
      }
    } catch {
      root.classList.remove("dark");
    }
  }, [pathname]);

  return <>{children}</>;
}
